import type { IDocumentAssemblerTemplateVersionPlaceholder } from '@commenda/api';

export interface IDocumentAssemblerVariable {
  instruction: string;
  label: string;
  count: number;
}

export interface IDocumentAssemblerVariableGroup {
  key: string;
  name: string;
  variables: IDocumentAssemblerVariable[];
}

export class DocumentAssemblerVariableHelper {
  /** A dotted key like `entity.jurisdiction`. Prose instructions such as `the client name` do not match. */
  static readonly DOTTED_INSTRUCTION_REGEX = /^[a-z0-9_]+(?:\.[a-z0-9_]+)+$/;

  /** Used when an instruction carries no group of its own, which is every prose-worded template. */
  static readonly UNGROUPED_KEY = '__ungrouped__';

  static readonly UNGROUPED_NAME = 'Variables';

  /** `entity.registration_number` -> `Registration number`; a prose instruction is already its own label. */
  static toLabel(instruction: string): string {
    const segment = this.DOTTED_INSTRUCTION_REGEX.test(instruction)
      ? (instruction.split('.').pop() ?? instruction)
      : instruction;
    const words = segment.replace(/_/g, ' ').trim();

    return words.charAt(0).toUpperCase() + words.slice(1);
  }

  /** `entity.jurisdiction` -> `Entity`. Null when the instruction is prose and names no group. */
  static toGroupKey(instruction: string): string | null {
    if (!this.DOTTED_INSTRUCTION_REGEX.test(instruction)) return null;

    return instruction.split('.')[0] ?? null;
  }

  static toGroupName(groupKey: string): string {
    const words = groupKey.replace(/_/g, ' ').trim();

    return words.charAt(0).toUpperCase() + words.slice(1);
  }

  /**
   * Groups the version's parsed placeholders for the rail. One row exists per occurrence, and the reader
   * confirms an instruction once however often it appears, so rows collapse onto their instruction and the
   * count is how many collapsed. Ordinal order is preserved, so the rail reads the way the document does.
   */
  static groupPlaceholders(
    placeholders: IDocumentAssemblerTemplateVersionPlaceholder[]
  ): IDocumentAssemblerVariableGroup[] {
    const countByInstruction = new Map<string, number>();
    [...placeholders]
      .sort((left, right) => left.ordinal - right.ordinal)
      .forEach((placeholder) => {
        countByInstruction.set(
          placeholder.instruction,
          (countByInstruction.get(placeholder.instruction) ?? 0) + 1
        );
      });

    const groups = new Map<string, IDocumentAssemblerVariableGroup>();

    [...countByInstruction].forEach(([instruction, count]) => {
      const groupKey = this.toGroupKey(instruction) ?? this.UNGROUPED_KEY;
      const existing = groups.get(groupKey);
      const variable = { instruction, label: this.toLabel(instruction), count };

      if (existing) {
        existing.variables.push(variable);

        return;
      }

      groups.set(groupKey, {
        key: groupKey,
        name:
          groupKey === this.UNGROUPED_KEY
            ? this.UNGROUPED_NAME
            : this.toGroupName(groupKey),
        variables: [variable],
      });
    });

    return [...groups.values()];
  }

  static matchesSearch(
    variable: IDocumentAssemblerVariable,
    search: string
  ): boolean {
    const needle = search.trim().toLowerCase();
    if (!needle) return true;

    return (
      variable.label.toLowerCase().includes(needle) ||
      variable.instruction.toLowerCase().includes(needle)
    );
  }
}
